import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowUpDown, Play, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useScan } from "@/contexts/ScanContext";

interface ScanRow {
  id: string;
  configName: string;
  status: string;
  totalLocations: number;
  sportFieldsFound: number;
  totalCost: number;
  durationMinutes: number;
  startedAt: string | null;
  completedAt: string | null;
}

interface ScanHistoryTableProps {
  data: ScanRow[];
}

type SortKey = keyof ScanRow;

function statusVariant(status: string) {
  switch (status) {
    case "completed":
      return "default" as const;
    case "running":
    case "in_progress":
    case "scanning":
    case "screenshotting":
    case "classifying":
    case "website_classifying":
      return "secondary" as const;
    case "failed":
    case "error":
      return "destructive" as const;
    case "stopped":
      return "outline" as const;
    default:
      return "outline" as const;
  }
}

function isResumable(status: string): boolean {
  return [
    "stopped",
    "failed",
    "keyword_scanning",
    "scanning",
    "web_researching",
    "website_classifying",
    "screenshotting",
    "classifying",
  ].includes(status);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ScanHistoryTable({ data }: ScanHistoryTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>("startedAt");
  const [sortAsc, setSortAsc] = useState(false);
  const { connect, setShowPanel } = useScan();
  const utils = trpc.useUtils();

  const resumeMutation = trpc.scans.resume.useMutation({
    onSuccess: ({ scanId }) => {
      connect(scanId);
      setShowPanel(true);
      utils.scans.list.invalidate();
      utils.scans.getActive.invalidate();
    },
  });

  if (!data.length) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
        No scan history yet
      </div>
    );
  }

  const sorted = [...data].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortAsc ? cmp : -cmp;
  });

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  }

  function SortableHead({ label, column }: { label: string; column: SortKey }) {
    return (
      <TableHead>
        <button
          type="button"
          className="flex items-center gap-1 hover:text-foreground"
          onClick={() => toggleSort(column)}
        >
          {label}
          <ArrowUpDown className="h-3 w-3" />
        </button>
      </TableHead>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableHead label="Date" column="startedAt" />
          <SortableHead label="Config" column="configName" />
          <TableHead>Status</TableHead>
          <SortableHead label="Locations" column="totalLocations" />
          <SortableHead label="Sport Fields" column="sportFieldsFound" />
          <SortableHead label="Cost" column="totalCost" />
          <SortableHead label="Duration" column="durationMinutes" />
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((row) => (
          <TableRow key={row.id}>
            <TableCell>{formatDate(row.startedAt)}</TableCell>
            <TableCell className="font-medium">{row.configName}</TableCell>
            <TableCell>
              <Badge variant={statusVariant(row.status)}>
                {row.status}
              </Badge>
            </TableCell>
            <TableCell>{row.totalLocations}</TableCell>
            <TableCell>{row.sportFieldsFound}</TableCell>
            <TableCell>${row.totalCost.toFixed(2)}</TableCell>
            <TableCell>
              {row.durationMinutes > 0 ? `${row.durationMinutes}m` : "-"}
            </TableCell>
            <TableCell>
              {isResumable(row.status) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => resumeMutation.mutate({ id: row.id })}
                  disabled={resumeMutation.isPending}
                >
                  {resumeMutation.isPending && resumeMutation.variables?.id === row.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  Resume
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
